import { Link, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  CreditCard,
  Facebook,
  FileText,
  Home,
  Instagram,
  LayoutDashboard,
  Linkedin,
  MessageSquare,
  Phone,
  Plus,
  ScanSearch,
  Settings,
  Shield,
  Signpost,
  Youtube,
} from "lucide-react";

import { cn } from "../../lib/utils";
import SidebarLogo from "../../assets/icons/ep-icon-blue.png";
import { Button } from "../common/customDesignedComponents/button";
import epLogo from "../../assets/new_ui_images/equity-protect-logo-full.png";
import { CUSTOMER_HOME_PATH } from "../helpers/constants";
import { isNativeAppShell } from "../helpers/utils";
import useBasicPlanOnlyCustomer from "../hooks/useBasicPlanOnlyCustomer";
import useCompleteProtectionCustomer from "../hooks/useCompleteProtectionCustomer";
import CallPilotChatButton from "../common/CallPilotChatButton";
import { userSettingsAction } from "../../redux/slices/user/userSettingSlice";

const MOBILE_SIDEBAR_BREAKPOINT = 699;

const navItems = [
  { name: "Account Profile", href: "/user-profile", icon: LayoutDashboard },
  { name: "Properties", href: "/properties", icon: Home },
  { name: "Scans", href: "/scans", icon: ScanSearch },
  { name: "Subscriptions", href: "/subscriptions", icon: CreditCard },
  { name: "Documents", href: "/documents", icon: FileText },
  { name: "Order Sign", href: "/order-sign", icon: Signpost, completeProtectionOnly: true },
  { name: "Communication", href: "/communication", icon: MessageSquare },
  { name: "Allow Access", href: "/sub-user", icon: Shield },
];

const BASIC_PLAN_HIDDEN_NAV = new Set(["Subscriptions", "Allow Access"]);

export const DashboardSidebar = ({
  isSidebarOpen = true,
  showIosAddProperty = false,
  onAddPropertyClick,
}: {
  isSidebarOpen?: boolean;
  showIosAddProperty?: boolean;
  onAddPropertyClick?: () => void;
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentPath = location.pathname;

  const { isBasicPlanOnly } = useBasicPlanOnlyCustomer();
  const { hasCompleteProtection } = useCompleteProtectionCustomer();

  const visibleNavItems = navItems.filter((item) => {
    if (isBasicPlanOnly && BASIC_PLAN_HIDDEN_NAV.has(item.name)) {
      return false;
    }
    if (item.completeProtectionOnly && !hasCompleteProtection) {
      return false;
    }
    return true;
  });

  const isActive = (path: string) => currentPath.startsWith(path);

  const closeSidebarOnMobile = () => {
    if (
      typeof window !== "undefined" &&
      window.innerWidth < MOBILE_SIDEBAR_BREAKPOINT
    ) {
      dispatch(userSettingsAction.setSidebarOpen(false));
    }
  };

  const navLinkClass = (path: string) =>
    cn(
      "flex items-center rounded-lg transition-colors",
      isActive(path)
        ? "bg-secondary/10 text-secondary"
        : "text-muted-foreground hover:bg-muted hover:text-foreground",
      isSidebarOpen
        ? "gap-3 px-3 py-2.5 text-sm font-medium"
        : "gap-0 px-0 py-2.5 justify-center w-full",
    );

  return (
    <aside
      className={`dashboard_sidebar bg-card border-r border-border flex flex-col h-screen fixed left-0 top-0 transition-all duration-200 z-10 ${
        isSidebarOpen ? "w-64 max-[698px]:z-50" : "w-16"
      }`}
      aria-hidden={!isSidebarOpen}
    >
      {/* Logo */}
      <div
        className={`border-b border-border flex items-center ${
          isSidebarOpen ? "p-4 justify-start" : "p-2 justify-center"
        }`}
      >
        <Link
          to={isNativeAppShell() ? CUSTOMER_HOME_PATH : "/"}
          onClick={closeSidebarOnMobile}
        >
          <img
            src={isSidebarOpen ? epLogo : SidebarLogo}
            alt="EquityProtect"
            className={isSidebarOpen ? "h-10 w-auto" : "h-7 w-auto"}
          />
        </Link>
      </div>

      {/* Navigation */}
      <nav className={isSidebarOpen ? "flex-1 p-4" : "flex-1 px-2 pt-4"}>
        <ul className={isSidebarOpen ? "space-y-1" : "space-y-3"}>
          {visibleNavItems.map((item) => (
            <li key={item.name}>
              <Link
                to={item.href}
                onClick={closeSidebarOnMobile}
                className={navLinkClass(item.href)}
              >
                <item.icon className="h-5 w-5 shrink-0" />
                {isSidebarOpen ? item.name : null}
              </Link>
              {showIosAddProperty && item.name === "Properties" ? (
                <button
                  type="button"
                  onClick={onAddPropertyClick}
                  className={cn(
                    "flex items-center rounded-lg transition-colors text-muted-foreground hover:bg-muted hover:text-foreground w-full mt-1 text-left border-0 bg-transparent cursor-pointer",
                    isSidebarOpen
                      ? "gap-3 px-3 py-2.5 text-sm font-medium"
                      : "gap-0 px-0 py-2.5 justify-center",
                  )}
                >
                  <Plus className="h-5 w-5 shrink-0" />
                  {isSidebarOpen ? "Add Property" : null}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </nav>

      {/* Bottom Section */}
      <div
        className={`border-t border-border ${
          isSidebarOpen
            ? "p-4 space-y-4"
            : "p-3 space-y-4 flex flex-col items-center"
        }`}
      >
        <Link
          to="/settings"
          onClick={closeSidebarOnMobile}
          className={navLinkClass("/settings")}
        >
          <Settings className="h-5 w-5 shrink-0" />
          {isSidebarOpen ? "Settings" : null}
        </Link>

        {/* Social Icons */}
        <div
          className={
            isSidebarOpen
              ? "flex items-center justify-start gap-3 px-3"
              : "flex flex-col items-center justify-center gap-3 px-0"
          }
        >
          <a
            href="https://www.instagram.com/equity.protect/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-7 h-7 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity"
          >
            <Instagram className="h-4 w-4" />
          </a>
          <a
            href="https://www.facebook.com/people/EquityProtect/100090626499488/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-7 h-7 bg-[#1877F2] rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity"
          >
            <Facebook className="h-4 w-4" />
          </a>
          <a
            href="https://www.linkedin.com/company/equity-protect"
            target="_blank"
            rel="noopener noreferrer"
            className="w-7 h-7 bg-[#0A66C2] rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity"
          >
            <Linkedin className="h-4 w-4" />
          </a>
          <a
            href="https://www.youtube.com/@equityprotect"
            target="_blank"
            rel="noopener noreferrer"
            className="w-7 h-7 bg-[#FF0000] rounded-full flex items-center justify-center text-white hover:opacity-80 transition-opacity"
          >
            <Youtube className="h-4 w-4" />
          </a>
        </div>

        <CallPilotChatButton isSidebarOpen={isSidebarOpen} />

        {/* Call Us Now Button */}
        <Button
          variant="secondary"
          className={
            isSidebarOpen
              ? "w-full gap-2 rounded-full"
              : "w-10 h-10 p-0 gap-0 rounded-full"
          }
          asChild
        >
          <a href="tel:8882132959">
            <Phone className="h-4 w-4" />
            {isSidebarOpen ? "CALL US NOW" : null}
          </a>
        </Button>
      </div>
    </aside>
  );
};
